import React from 'react';
import { Flex, InputNumber } from 'antd';
import { faCircle, faCircleDot, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExText } from 'jf/components/DevExText';
import { useTargetingClient } from 'jf/services/useTargetingClient';

import { useStudyBuilderState } from '../StudyBuilderState';
import { useStudyUpdater } from '../useStudyUpdater';

import { TargetingCard } from './TargetingCard';
import { useStudySlackTargetUpdater } from './useStudySlackTargetUpdater';

export const TargetRespondentStep: React.FC = () => {
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const studyUpdater = useStudyUpdater(state.studyRef);

  const { useGetStatus } = useTargetingClient();
  const studySlackTargetUpdater = useStudySlackTargetUpdater(state.studyRef);
  const { data: messengerStatus, isError: isIntegrationError } = useGetStatus();

  if (!messengerStatus && !isIntegrationError) {
    return <DevExLoader />;
  }

  return (
    <Flex
      vertical
      style={{ fontSize: theme.variable.fontSize.md, lineHeight: theme.variable.lineHeight }}
      gap={theme.variable.spacing.lg}
      data-pendo={!messengerStatus?.connected ? 'no-integration' : undefined}
    >
      <div style={{ fontSize: theme.variable.fontSize.xl }}>Select Recipients</div>
      <div>Choose how you want to distribute this survey after publishing.</div>

      <DevExCard
        onClick={
          state.slackTargetingEnabled || state.msteamsTargetingEnabled
            ? () => {
                // delete all existing targets and wipe state for other components
                studySlackTargetUpdater.update([]);
                state.update({
                  slackTargetingEnabled: false,
                  msteamsTargetingEnabled: false,
                  enableScheduledReminders: false,
                  slackTargets: [],
                  slackUsers: [],
                  msteamsTargets: [],
                  msteamsUsers: [],
                });
                studyUpdater.update({
                  enableScheduledReminders: false,
                  scheduledCloseDate: null,
                });
              }
            : undefined
        }
        title={
          <>
            <FontAwesomeIcon
              icon={
                !state.slackTargetingEnabled && !state.msteamsTargetingEnabled
                  ? faCircleDot
                  : faCircle
              }
              color={theme.color.brand.active}
            />
            I'll share the link myself
          </>
        }
      >
        {!state.slackTargetingEnabled && !state.msteamsTargetingEnabled ? (
          <>
            <DevExText>Approximately how many people will receive this survey?</DevExText>
            <Flex align="center" gap={theme.variable.spacing.sm}>
              <InputNumber
                placeholder="0"
                onChange={(value) => {
                  const newValue = value ? parseInt(value) : null;
                  state.update({ targetResponses: newValue });
                  studyUpdater.update({ targetResponses: newValue });
                }}
                value={state.targetResponses?.toString() ?? ''}
                min={'0'}
              />
              <DevExText fontSize="md">total recipients</DevExText>
            </Flex>
            <DevExText type="secondary" lined>
              <FontAwesomeIcon
                icon={faInfoCircle}
                style={{
                  color: theme.color.text.secondary,
                }}
              />{' '}
              Providing an estimated number of recipients will enable you to monitor response rate
              in real-time.
            </DevExText>
          </>
        ) : (
          <DevExText type="secondary" fontSize="md">
            Select this option to manage your survey's distribution yourself.
          </DevExText>
        )}
      </DevExCard>
      <TargetingCard messengerStatus={messengerStatus} isIntegrationError={isIntegrationError} />
    </Flex>
  );
};
